/**
 * Impersonation Banner Component
 * 
 * Zeigt oben im Dashboard wenn Super Admin als Tenant eingeloggt ist:
 * - Tenant Name
 * - Countdown (15 Minuten)
 * - "Zurück zu Admin" Button
 * - Auto-Logout bei Ablauf
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, LogOut, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ImpersonationBanner() {
  const navigate = useNavigate();
  const [impersonation, setImpersonation] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [mode, setMode] = useState('readonly');
  const [extending, setExtending] = useState(false);
  const [enablingWrite, setEnablingWrite] = useState(false);

  useEffect(() => {
    // Lade Impersonation Info aus localStorage
    const impersonationData = localStorage.getItem('cashx_impersonation');
    if (impersonationData) {
      try {
        const data = JSON.parse(impersonationData);
        setImpersonation(data);
        setMode(data.mode || 'readonly');
        
        // Berechne verbleibende Zeit
        const expiresAt = new Date(data.expires_at);
        const now = new Date();
        const secondsLeft = Math.max(0, Math.floor((expiresAt - now) / 1000));
        setTimeLeft(secondsLeft);
      } catch (e) {
        console.error('Failed to parse impersonation data:', e);
      }
    }
  }, []);

  useEffect(() => {
    if (!impersonation || timeLeft <= 0) return;

    // Countdown Timer
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        const newTime = prev - 1;
        
        // Auto-Logout bei Ablauf
        if (newTime <= 0) {
          handleExitImpersonation(true);
          return 0;
        }
        
        // Warnung bei 60 Sekunden
        if (newTime === 60) {
          toast.warning('Impersonation läuft in 1 Minute ab!');
        }
        
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [impersonation, timeLeft]);

  const handleExitImpersonation = async (autoLogout = false) => {
    setExiting(true);
    
    try {
      // Session beenden
      const session_id = impersonation?.session_id;
      if (session_id) {
        try {
          const adminToken = localStorage.getItem('cashx_admin_token');
          await fetch(`${API_URL}/api/admin/impersonate/${session_id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${adminToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ ended_by: autoLogout ? 'timeout' : 'admin' })
          });
        } catch (e) {
          console.error('Failed to end session:', e);
        }
      }
      
      // Restore Admin Token
      const adminToken = localStorage.getItem('cashx_admin_token');
      const adminUser = localStorage.getItem('cashx_admin_user');
      
      if (adminToken) {
        localStorage.setItem('access_token', adminToken);
      }
      if (adminUser) {
        localStorage.setItem('cashx_user', adminUser);
      }
      
      // Cleanup
      localStorage.removeItem('cashx_admin_token');
      localStorage.removeItem('cashx_admin_user');
      localStorage.removeItem('cashx_impersonation');
      
      if (autoLogout) {
        toast.error('Impersonation-Session abgelaufen');
      } else {
        toast.success('Impersonation beendet');
      }
      
      // Zurück zu Admin
      navigate('/admin');
      window.location.reload();
      
    } catch (error) {
      console.error('Exit impersonation error:', error);
      toast.error('Fehler beim Beenden der Session');
    } finally {
      setExiting(false);
    }
  };
  
  const handleEnableWriteAccess = async () => {
    if (!confirm('Wirklich Write Access aktivieren? Alle Aktionen werden geloggt.')) {
      return;
    }
    
    setEnablingWrite(true);
    try {
      const adminToken = localStorage.getItem('cashx_admin_token');
      const session_id = impersonation?.session_id;
      
      const resp = await fetch(`${API_URL}/api/admin/impersonate/${session_id}/enable-write-access`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (resp.ok) {
        setMode('write_access');
        
        // Update localStorage
        const updatedImpersonation = { ...impersonation, mode: 'write_access' };
        localStorage.setItem('cashx_impersonation', JSON.stringify(updatedImpersonation));
        
        toast.success('Write Access aktiviert');
      } else {
        toast.error('Write Access aktivieren fehlgeschlagen');
      }
    } catch (e) {
      console.error('Enable write access error:', e);
      toast.error('Fehler beim Aktivieren');
    } finally {
      setEnablingWrite(false);
    }
  };
  
  const handleExtendSession = async () => {
    setExtending(true);
    try {
      const adminToken = localStorage.getItem('cashx_admin_token');
      const session_id = impersonation?.session_id;
      
      const resp = await fetch(`${API_URL}/api/admin/impersonate/${session_id}/extend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (resp.ok) {
        const result = await resp.json();
        const newExpiresAt = new Date(result.expires_at);
        const now = new Date();
        const secondsLeft = Math.max(0, Math.floor((newExpiresAt - now) / 1000));
        
        setTimeLeft(secondsLeft);
        
        // Update localStorage
        const updatedImpersonation = { ...impersonation, expires_at: result.expires_at };
        localStorage.setItem('cashx_impersonation', JSON.stringify(updatedImpersonation));
        
        toast.success('Session um 15 Minuten verlängert');
      } else {
        toast.error('Session verlängern fehlgeschlagen');
      }
    } catch (e) {
      console.error('Extend session error:', e);
      toast.error('Fehler beim Verlängern');
    } finally {
      setExtending(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!impersonation) return null;

  const isExpiringSoon = timeLeft <= 120; // Letzte 2 Minuten
  const isReadOnly = mode === 'readonly';
  const startTime = impersonation.start_time || impersonation.started_at;
  const formattedStartTime = startTime ? new Date(startTime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-50 border-b-2 ${
        isExpiringSoon 
          ? 'bg-red-600/95 border-red-500' 
          : 'bg-amber-600/95 border-amber-500'
      }`}
      data-testid="impersonation-banner"
      style={{ backdropFilter: 'blur(10px)' }}
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Info */}
          <div className="flex items-center gap-3">
            <Shield className="h-5 w-5 text-white flex-shrink-0" />
            <div>
              <p className="font-bold text-white text-sm">
                🔍 Support ist eingeloggt: admin@c***x.de (seit {formattedStartTime} Uhr)
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-white/80 text-xs">
                  Modus:
                </span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                  isReadOnly 
                    ? 'bg-blue-500/30 text-blue-200' 
                    : 'bg-green-500/30 text-green-200'
                }`}>
                  {isReadOnly ? 'Read-Only' : 'Write Access'}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Countdown */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
              isExpiringSoon 
                ? 'bg-red-700/50' 
                : 'bg-amber-700/50'
            }`}>
              <Clock className="h-4 w-4 text-white" />
              <span className="font-mono font-bold text-white">
                {formatTime(timeLeft)}
              </span>
            </div>
            
            {/* Write Access Button (nur wenn readonly) */}
            {isReadOnly && (
              <Button
                onClick={handleEnableWriteAccess}
                disabled={enablingWrite}
                size="sm"
                className="bg-green-600/80 hover:bg-green-600 text-white"
                data-testid="enable-write-access-button"
              >
                {enablingWrite ? 'Aktiviere...' : 'Write Access'}
              </Button>
            )}
            
            {/* Extend Session Button */}
            <Button
              onClick={handleExtendSession}
              disabled={extending}
              size="sm"
              className="bg-white/20 hover:bg-white/30 text-white border border-white/30"
              data-testid="extend-session-button"
            >
              {extending ? 'Verlängere...' : '+15 Min'}
            </Button>

            {/* Exit Button */}
            <Button
              onClick={() => handleExitImpersonation(false)}
              disabled={exiting}
              size="sm"
              className="bg-white/20 hover:bg-white/30 text-white border border-white/30"
              data-testid="exit-impersonation-button"
            >
              {exiting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Beende...
                </>
              ) : (
                <>
                  <LogOut className="h-4 w-4 mr-2" />
                  Zurück
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
